import React from 'react';
import { QRCodeCanvas } from 'qrcode.react';

import Icon from 'flow/components/ui-components/Icon';
import useAppContext from 'flow/hooks/useAppContext';
import { INTERNATIONAL_CONFERENCE_NUMBER } from 'flow/utils/meeting-constants';

const QR_SIZE = 150;

interface Props {
    dialInNumber?: string;
    pin?: string;
    conferenceLink?: string;
}

/**
 * The codes for continuing on another device: dial in, or open the meeting.
 *
 * Shared by the two places that offer this, which differ only in how they are
 * presented — a card floating over the meeting, a dialog over the lobby. Each
 * code appears only once the thing it encodes is known, so the row can hold
 * one or both.
 */
const MeetingJoinOnAnotherDeviceCodes = ({
    dialInNumber,
    pin,
    conferenceLink,
}: Props) => {
    const { i18next } = useAppContext();

    // Dialling in needs a PIN; the number itself has a fallback, as the joining
    // info elsewhere does. A guest's lobby is not told a local number, so
    // requiring one here left them with no way to dial in at all.
    const canCallIn = !!pin;
    const callInNumber = dialInNumber || INTERNATIONAL_CONFERENCE_NUMBER;
    const canJoinWithVideo = !!conferenceLink;

    return (
        <div className="flex gap-4">
            {canCallIn && (
                <div className="box-border flex grow flex-col items-center rounded-sm border">
                    <div className="flex w-full items-center justify-center gap-2 border-b px-4 py-2 font-bold">
                        <Icon iconKey="phone" size="1rem" />
                        {i18next.t('videoconference.join_info_call_in')}
                    </div>
                    <div className="flex w-full justify-center p-3">
                        <QRCodeCanvas
                            value={`tel:${callInNumber},${pin}`}
                            size={QR_SIZE}
                        />
                    </div>
                </div>
            )}

            {canJoinWithVideo && (
                <div className="box-border flex grow flex-col items-center rounded-sm border">
                    <div className="flex w-full items-center justify-center gap-2 border-b px-4 py-2 font-bold">
                        <Icon iconKey="video" size="1rem" />
                        {i18next.t('videoconference.join_info_join_with_video')}
                    </div>
                    <div className="flex w-full justify-center p-3">
                        <QRCodeCanvas value={conferenceLink} size={QR_SIZE} />
                    </div>
                </div>
            )}
        </div>
    );
};

export default MeetingJoinOnAnotherDeviceCodes;
